import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "../../components/app/AppShell";
import { FullPageLoading } from "../../components/Loading";
import {
  listProfiles,
  listTranscripts,
  type ProfileSummary,
  type TranscriptItem,
} from "../../lib/api";
import { formatTimestamp } from "../../lib/format";

// Speaker-attributed transcript with an aria-hidden fake waveform. No real
// audio wiring yet — see TODO below.

export default function AppCallDetail() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [transcript, setTranscript] = useState<TranscriptItem | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0] || null;
        setProfile(p);
        if (p && id) {
          const t = await listTranscripts(p.phone, { includeText: true });
          const match = (t.transcripts || []).find(
            (x) => x.transcriptId === id
          );
          setTranscript(match || null);
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Fehler beim Laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <FullPageLoading />;

  const firstName =
    profile?.interviewee_name?.split(" ")[0] || "der Erzählerin";

  // Parse the transcript into alternating turns. The /me/transcripts endpoint
  // returns text-only for now, so we split on blank lines as a best-effort
  // segmentation. Each block alternates between speaker (Konrad/Ida) and the
  // narrator turn-by-turn.
  const turns = parseTranscript(transcript?.transcript || "");

  return (
    <AppShell
      eyebrow={
        transcript
          ? `${formatTimestamp(transcript.timestamp) || "Anruf"}${
              typeof transcript.duration_seconds === "number"
                ? ` · ${Math.round(transcript.duration_seconds / 60)} Min.`
                : ""
            }`
          : "Anruf"
      }
      title={
        <>
          Anruf <em>mit {firstName}</em>
        </>
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(107,32,24,0.08)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderLeft: "3px solid var(--oxblood)",
              borderRadius: 4,
              padding: "10px 14px",
              color: "var(--oxblood-d)",
              fontFamily: "var(--f-body)",
              fontSize: 14,
              marginBottom: 18,
            }}
          >
            {error}
          </div>
        )}

        {/* Fake waveform + transport — aria-hidden because no real audio yet.
            TODO(Phase C): wire real audio playback against signed URL. */}
        <div
          aria-hidden="true"
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 16,
            padding: "20px 20px 18px",
            marginBottom: 22,
          }}
        >
          <FakeWaveform />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontFamily: "var(--f-ui)",
              fontSize: 11,
              color: "var(--ink-faint)",
              marginTop: 8,
              marginBottom: 14,
            }}
          >
            <span>00:00</span>
            <span>
              {transcript?.duration_seconds
                ? formatSeconds(transcript.duration_seconds)
                : "12:34"}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 22,
            }}
          >
            <button
              type="button"
              tabIndex={-1}
              style={{
                background: "transparent",
                border: "none",
                padding: 6,
                cursor: "default",
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="var(--ink-soft)"
              >
                <path d="M11 19V5l-7 7zM20 19V5l-7 7z" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "var(--oxblood)",
                border: "none",
                boxShadow: "0 8px 20px rgba(107,32,24,0.30)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "default",
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="var(--paper-50)"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
            <button
              type="button"
              tabIndex={-1}
              style={{
                background: "transparent",
                border: "none",
                padding: 6,
                cursor: "default",
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="var(--ink-soft)"
              >
                <path d="M13 5l7 7-7 7zM4 5l7 7-7 7z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Transcript */}
        <div className="t-caps" style={{ marginBottom: 10 }}>
          Transkript
        </div>
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 12,
            padding: "16px 18px",
          }}
        >
          {turns.length === 0 ? (
            <div
              style={{
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 14.5,
                color: "var(--ink-faint)",
                padding: "12px 0",
              }}
            >
              Für diesen Anruf liegt noch kein Transkript vor.
            </div>
          ) : (
            turns.map((turn, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 12,
                  padding: "10px 0",
                  borderBottom:
                    i < turns.length - 1
                      ? "1px dashed rgba(43,26,15,0.10)"
                      : "none",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background:
                      turn.who === "narrator"
                        ? "var(--oxblood)"
                        : "rgba(168,122,58,0.22)",
                    color:
                      turn.who === "narrator"
                        ? "var(--paper-50)"
                        : "var(--ink)",
                    fontFamily: "var(--f-display)",
                    fontStyle: "italic",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {turn.who === "narrator"
                    ? firstName.charAt(0).toUpperCase() || "E"
                    : "K"}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontFamily: "var(--f-ui)",
                      fontSize: 10.5,
                      color: "var(--ink-faint)",
                      letterSpacing: "0.10em",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    {turn.who === "narrator" ? "Erzähler:in" : "Konrad / Ida"}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--f-body)",
                      fontSize: 15,
                      lineHeight: 1.5,
                      color: "var(--ink)",
                      marginTop: 3,
                      background: turn.highlight
                        ? "rgba(168,122,58,0.22)"
                        : "transparent",
                      borderRadius: 3,
                      padding: turn.highlight ? "2px 4px" : 0,
                      margin: turn.highlight ? "3px -4px 0" : "3px 0 0",
                    }}
                  >
                    {turn.text}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AppShell>
  );
}

// 180 vertical bars at varying heights, fully decorative.
function FakeWaveform() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 1,
        height: 50,
      }}
      aria-hidden
    >
      {Array.from({ length: 180 }).map((_, i) => {
        const seed = Math.sin(i * 1.37) * Math.cos(i * 0.51);
        const h = 6 + Math.abs(seed) * 42;
        const played = i / 180 < 0.32;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              height: h,
              borderRadius: 1,
              background: played ? "var(--oxblood)" : "rgba(43,26,15,0.18)",
            }}
          />
        );
      })}
    </div>
  );
}

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

type Turn = { who: "interviewer" | "narrator"; text: string; highlight?: boolean };

function parseTranscript(raw: string): Turn[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  // Split on blank lines OR newlines for simple alternation. The backend
  // doesn't currently mark speakers — we alternate interviewer / narrator.
  const parts = trimmed
    .split(/\n\s*\n+|\n/g)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.map((text, i) => ({
    who: i % 2 === 0 ? "interviewer" : "narrator",
    text,
    // Highlight the second narrator turn as the "interesting excerpt" example.
    highlight: i === 3,
  }));
}
